import { Button, Popconfirm, Select, Space, Table, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, FilterOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCandidateList, getJobList, deleteCandidate, CANDIDATE_STAGES, stageIdToKey } from "../../redux/rtk/features/hiring/hiringSlice";
import HiringCandidateForm from "./HiringCandidateForm";
import FilterPanel from "./FilterPanel";
import "./Hiring.css";
import "./FilterPanel.css";

const HiringCandidateList = () => {
	const dispatch = useDispatch();
	const [formVisible, setFormVisible] = useState(false);
	const [editingCandidate, setEditingCandidate] = useState(null);
	const [filterOpen, setFilterOpen] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [filterStatus, setFilterStatus] = useState("");
	const [filterJobId, setFilterJobId] = useState("");
	const { candidateList, jobList, loading } = useSelector((state) => state.hiring);

	useEffect(() => {
		dispatch(getJobList());
	}, [dispatch]);

	useEffect(() => {
		dispatch(getCandidateList());
	}, [dispatch]);

	const handleAdd = () => {
		setEditingCandidate(null);
		setFormVisible(true);
	};

	const handleEdit = (record) => {
		setEditingCandidate(record);
		setFormVisible(true);
	};

	const handleFormSuccess = () => {
		setFormVisible(false);
		setEditingCandidate(null);
		dispatch(getCandidateList());
	};

	const handleDelete = (record) => {
		dispatch(deleteCandidate(record)).then(() => dispatch(getCandidateList()));
	};

	const getStageTag = (stageOrStageId) => {
		const key = typeof stageOrStageId === "number" ? stageIdToKey(stageOrStageId) : String(stageOrStageId ?? "").toUpperCase();
		const config = CANDIDATE_STAGES.find((c) => c.key === key) || { label: key || "—", color: "default" };
		return <Tag color={config.color}>{config.label}</Tag>;
	};

	const fullName = (record) => {
		const first = record.FirstName ?? record.firstName ?? "";
		const middle = record.MiddleName ?? record.middleName ?? "";
		const last = record.LastName ?? record.lastName ?? "";
		return [first, middle, last].filter(Boolean).join(" ") || (record.name ?? record.Name ?? record.candidateName ?? "—");
	};

	const jobOptions = (jobList || []).map((j) => ({
		value: j.JobOpeningId ?? j.id ?? j.JobId,
		label: j.Title ?? j.title ?? j.JobTitle ?? j.jobTitle ?? `Job #${j.JobOpeningId ?? j.id ?? j.JobId}`,
	}));

	const columns = [
		{
			title: "Name",
			key: "name",
			render: (_, record) => fullName(record),
		},
		{
			title: "Email",
			dataIndex: "Email",
			key: "email",
			render: (val, record) => record.Email ?? record.email ?? "—",
		},
		{
			title: "Phone",
			dataIndex: "Phone",
			key: "phone",
			render: (val, record) => record.Phone ?? record.phone ?? "—",
		},
		{
			title: "Job",
			key: "jobTitle",
			render: (val, record) =>
				record.JobTitle ?? record.jobTitle ?? record.JobOpeningTitle ?? record.job?.Title ?? "—",
		},
		{
			title: "Source",
			dataIndex: "Source",
			key: "source",
			render: (val, record) => record.Source ?? record.source ?? "—",
		},
		{
			title: "Stage",
			key: "stage",
			render: (val, record) => getStageTag(record.StageId ?? record.stageId ?? record.stage ?? record.Stage),
			width: 120,
		},
		{
			title: "Actions",
			key: "action",
			width: 140,
			render: (_, record) => (
				<Space>
					<Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
						Edit
					</Button>
					<Popconfirm
						title="Delete this candidate?"
						description="This will mark the candidate as deleted."
						onConfirm={() => handleDelete(record)}
						okText="Delete"
						cancelText="Cancel"
						okButtonProps={{ danger: true }}
					>
						<Button type="link" size="small" danger icon={<DeleteOutlined />}>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	];

	const rawList = candidateList || [];
	const filteredList = rawList.filter((c) => {
		if (filterJobId && (c.JobOpeningId ?? c.jobOpeningId ?? c.jobId ?? c.JobId) !== filterJobId) return false;
		const stageKey = c.StageId != null ? stageIdToKey(c.StageId) : String(c.stage ?? c.Stage ?? "").toUpperCase();
		if (filterStatus && stageKey !== filterStatus) return false;
		if (searchText.trim()) {
			const q = searchText.trim().toLowerCase();
			const name = fullName(c).toLowerCase();
			const email = (c.Email ?? c.email ?? "").toLowerCase();
			const phone = (c.Phone ?? c.phone ?? "").toLowerCase();
			if (!name.includes(q) && !email.includes(q) && !phone.includes(q)) return false;
		}
		return true;
	});
	const dataSource = filteredList.map((item, index) => ({
		...item,
		key: item.CandidateId ?? item.id ?? item.candidateId ?? index,
	}));

	const stageFilterOptions = [
		{ value: "", label: "All Status" },
		...CANDIDATE_STAGES.map((s) => ({ value: s.key, label: s.label })),
	];
	const jobFilterOptions = [
		{ value: "", label: "All Types" },
		...jobOptions.map((o) => ({ value: o.value, label: o.label })),
	];

	return (
		<>
			<div className="flex justify-between items-center mb-4 flex-wrap gap-2 filter-panel-wrap">
				<span className="text-gray-600">Candidates.</span>
				<Space>
					<button
						type="button"
						className="filter-panel-trigger"
						onClick={() => setFilterOpen((o) => !o)}
						aria-label="Filters"
					>
						<FilterOutlined style={{ fontSize: 16 }} />
					</button>
					<FilterPanel
						open={filterOpen}
						onClose={() => setFilterOpen(false)}
						search={{
							placeholder: "Search by name, email, phone",
							value: searchText,
							onChange: setSearchText,
						}}
						filters={[
							{
								key: "status",
								label: "Status",
								placeholder: "All Status",
								value: filterStatus,
								onChange: setFilterStatus,
								options: stageFilterOptions,
							},
							{
								key: "job",
								label: "Job",
								placeholder: "All Types",
								value: filterJobId,
								onChange: setFilterJobId,
								options: jobFilterOptions,
							},
						]}
					/>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={handleAdd}
						className="hiring-btn-green"
					>
						Add candidate
					</Button>
				</Space>
			</div>
			<Table
				columns={columns}
				dataSource={dataSource}
				loading={loading}
				pagination={{ pageSize: 10 }}
				locale={{ emptyText: "No candidates yet. Add a candidate or select a job." }}
			/>
			<HiringCandidateForm
				open={formVisible}
				onClose={() => {
					setFormVisible(false);
					setEditingCandidate(null);
				}}
				onSuccess={handleFormSuccess}
				initialValues={editingCandidate}
				jobList={jobList}
			/>
		</>
	);
};

export default HiringCandidateList;
